/**
 * Voice leaf — the visualizer mount for the `showVoiceAnimation` branch.
 *
 * Web has two leaves here, both gated on LiveKit's `RoomContext`. RN has one,
 * and no context at all:
 *
 *  - the Room arrives as a prop. The widget already holds it (the transport
 *    returns it for exactly these presentational reads), so wrapping the tree
 *    in a second provider just to read two track handles buys nothing. Without
 *    a room the visualizer renders with no tracks, which is its ambient idle
 *    state — the degradation is invisible.
 *  - there is no `MaybeRoomAudioRenderer` counterpart. Remote audio needs no
 *    elements on mobile: subscribed tracks play through the OS audio session,
 *    which `transport/livekit-transport.ts` owns for the life of the call.
 *
 * Track handles are read imperatively rather than through hooks, so they have
 * to be re-read whenever publications change — a call that starts before the
 * agent joins would otherwise animate one side forever.
 */

import { useEffect, useState, type FC } from "react";
import { StyleSheet, type StyleProp, type ViewStyle } from "react-native";
import { RoomEvent, Track, type LocalAudioTrack, type RemoteAudioTrack, type Room } from "livekit-client";

import { isAgentParticipant } from "../transport/agent";
import DotAudioVisualizer from "./dot-audio-visualizer";

interface VoiceAnimationLeafProps {
  /** The live Room, when there is one. Absent → ambient idle animation. */
  room?: Room;
  style?: StyleProp<ViewStyle>;
}

interface MicrophoneTracks {
  localTrack?: LocalAudioTrack;
  remoteTrack?: RemoteAudioTrack;
}

/** Every event after which the microphone publications may differ. */
const TRACK_EVENTS = [RoomEvent.TrackSubscribed, RoomEvent.TrackUnsubscribed, RoomEvent.LocalTrackPublished, RoomEvent.LocalTrackUnpublished, RoomEvent.ParticipantConnected] as const;

/**
 * Mic publications on both sides (App.tsx:236-241). The agent is resolved with
 * the shared adapter rule so the visualizer always animates the same
 * participant the transport talks to.
 */
const resolveTracks = (room: Room | undefined): MicrophoneTracks => {
  if (!room) return {};
  const agent = Array.from(room.remoteParticipants.values()).find(isAgentParticipant);
  // Casts are safe by construction: a `Track.Source.Microphone` publication is
  // always an audio track — the same narrowing the web leaf makes.
  return {
    localTrack: room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track as LocalAudioTrack | undefined,
    remoteTrack: agent?.getTrackPublication(Track.Source.Microphone)?.track as RemoteAudioTrack | undefined,
  };
};

const useMicrophoneTracks = (room: Room | undefined): MicrophoneTracks => {
  const [tracks, setTracks] = useState<MicrophoneTracks>(() => resolveTracks(room));

  useEffect(() => {
    // Re-read on mount too: a track may have been published between the render
    // that produced the initial state and this effect running.
    setTracks(resolveTracks(room));
    if (!room) return;

    const update = () => setTracks(resolveTracks(room));
    for (const event of TRACK_EVENTS) room.on(event, update);
    return () => {
      for (const event of TRACK_EVENTS) room.off(event, update);
    };
  }, [room]);

  return tracks;
};

export const VoiceAnimationLeaf: FC<VoiceAnimationLeafProps> = ({ room, style }) => {
  const { localTrack, remoteTrack } = useMicrophoneTracks(room);
  return <DotAudioVisualizer localTrack={localTrack} remoteTrack={remoteTrack} style={[styles.fill, style]} />;
};

const styles = StyleSheet.create({
  fill: { flex: 1 },
});
